/**
 * Stable colour assignment for PTN sources (encoders) on the Remote Control
 * page. Each encoder gets a distinct colour; screens (decoders) routed to that
 * encoder render the matching colour. Assignment is by the encoder's index in
 * the routing-state `encoders` list (backend returns it sorted by name), so
 * SourcesPanel and ScreenGrid — both reading the same cached routing-state —
 * derive identical colours without sharing component state.
 */
export const SOURCE_PALETTE = [
  '#ef4444', // red
  '#3b82f6', // blue
  '#22c55e', // green
  '#f59e0b', // orange
  '#a855f7', // purple
  '#ec4899', // pink
  '#14b8a6', // teal
  '#eab308', // amber
];

/**
 * Build encoderId → colour from the routing-state `encoders` array.
 * @param {Array<{id:string,name:string}>} encoders
 * @returns {Record<string,string>}
 */
export function buildEncoderColorMap(encoders = []) {
  const map = {};
  encoders.forEach((e, i) => {
    if (e?.id) map[e.id] = SOURCE_PALETTE[i % SOURCE_PALETTE.length];
  });
  return map;
}

/**
 * screenId → encoderId from the routing-state `routes` array (skips unrouted).
 * @param {Array<{screen_id:string,encoder_id:?string}>} routes
 * @returns {Record<string,string>}
 */
export function buildScreenSourceMap(routes = []) {
  const map = {};
  routes.forEach((r) => {
    if (r?.screen_id && r?.encoder_id) map[r.screen_id] = r.encoder_id;
  });
  return map;
}

/** Normalize the routing-state response (tolerates {data:{...}} wrapping). */
export function unwrapRoutingState(data) {
  const p = data?.data ?? data ?? {};
  return {
    encoders: Array.isArray(p.encoders) ? p.encoders : [],
    routes: Array.isArray(p.routes) ? p.routes : [],
  };
}
